import { Select as AntdSelect, Flex, Tooltip } from 'antd';
import type { SelectProps as AntdSelectProps, DefaultOptionType } from 'antd/lib/select';
import { useMemo, useState, type FC } from 'react';
import { Text } from '@shared/ui/antd';
import i18n from '@shared/services/i18n/i18n';

interface SelectProps extends AntdSelectProps {
  selectMode?: 'tags' | 'multiple' | 'undefined';
  isSingleSelectMode?: boolean;
  hasSearch?: boolean;
  isDisabled?: boolean;
  dataTestId?: string;
  onSearch?: (value: string) => void;
  onChange?: (value: any, option?: DefaultOptionType | DefaultOptionType[]) => void;
}

const handleSingleSelectChange = (
  newValue: unknown,
  option: DefaultOptionType | DefaultOptionType[] | undefined,
  onChange?: (value: any, option?: DefaultOptionType | DefaultOptionType[]) => void,
) => {
  const cleaned = (Array.isArray(newValue) ? newValue : [newValue]).filter(Boolean);
  const latestValue = cleaned[cleaned.length - 1];
  const normalizedOption = Array.isArray(option) ? option[option.length - 1] : option;

  const resultValue = latestValue === undefined ? [] : [latestValue];
  onChange?.(resultValue, normalizedOption);
};
export const Select: FC<SelectProps> = ({
  selectMode = 'tags',
  isSingleSelectMode,
  hasSearch = false,
  value,
  isDisabled,
  onChange,
  dataTestId,
  ...selectProps
}) => {
  const [internalSearchValue, setInternalSearchValue] = useState<string>('');

  const mergedSearchValue = useMemo(
    () =>
      typeof selectProps.searchValue === 'string' ? selectProps.searchValue : internalSearchValue,
    [selectProps.searchValue, internalSearchValue],
  );

  const currentMode = selectMode === 'undefined' ? undefined : selectMode;
  const currentValue = isSingleSelectMode && Array.isArray(value) ? value.slice(-1) : value;
  const currentOnChange = isSingleSelectMode
    ? (newValue: unknown, option?: DefaultOptionType | DefaultOptionType[]) =>
        handleSingleSelectChange(newValue, option ?? [], onChange)
    : onChange;
  const isSearchEnabled = Boolean((selectProps.showSearch ?? hasSearch) || selectProps.onSearch);

  const shouldHideTag = isSingleSelectMode && !!mergedSearchValue;

  const handleSearch = (val: string) => {
    setInternalSearchValue(val);
    selectProps.onSearch?.(val);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) setInternalSearchValue('');
    selectProps.onDropdownVisibleChange?.(open);
  };

  return (
    <AntdSelect
      mode={currentMode}
      tagRender={(props) =>
        isSingleSelectMode ? (
          <Flex
            style={{
              padding: '0 11px',
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              alignItems: 'center',
              display: shouldHideTag ? 'none' : undefined,
            }}
          >
            {props.label}
          </Flex>
        ) : (
          <span style={shouldHideTag ? { display: 'none' } : undefined}>{props.label}</span>
        )
      }
      autoClearSearchValue
      menuItemSelectedIcon={<></>}
      dropdownRender={(menu) => (
        <Flex style={{ padding: '8px' }} gap="4px" vertical>
          {menu}
        </Flex>
      )}
      optionRender={(option) => (
        <Flex style={{ height: '100%' }} align="center">
          <Text variant="sm" fontWeight={400} style={{ color: 'var(--color-text-secondary)' }}>
            {option.label}
          </Text>
        </Flex>
      )}
      maxTagCount={!isSingleSelectMode ? 0 : undefined}
      maxTagPlaceholder={(omittedValues) => (
        <Tooltip
          styles={{ root: { pointerEvents: 'none' } }}
          title={omittedValues.map(({ label }) => label).join(', ')}
        >
          <Text variant="md" fontWeight={400}>
            {i18n.t('common.attachmentForm.selected', {
              count: omittedValues.length,
            })}
          </Text>
        </Tooltip>
      )}
      data-testid={dataTestId}
      value={currentValue}
      onChange={currentOnChange}
      disabled={isDisabled}
      showSearch={isSearchEnabled}
      searchValue={isSearchEnabled ? mergedSearchValue : undefined}
      onSearch={isSearchEnabled ? handleSearch : undefined}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      {...selectProps}
    />
  );
};
